import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { OrgInternalBrandPair } from 'src/app/core/models/transport-operation/org-internal-brand.model';
import { ValidationResult } from 'src/app/core/models/validation-result.model';

@Injectable({
  providedIn: 'root'
})
export class OrgBrandMappingService {

  private readonly baseUrl = `${environment.logisticsApiUrl}/orgInternalBrandMapping`;

  constructor(private readonly http: HttpClient) {}

  // =================================================================
  // Region: 查詢與驗證 (Read Operations)
  // =================================================================

  /**
   * 取得指定 Org 可用的品牌代碼清單
   * 對應後端: GET /{orgId}/codes
   * @param orgId 公司代碼
   * @returns string[] (e.g. ['Samsung Semco', 'Samsung'])
   */
  getAllowedBrandCodes(orgId: string): Observable<string[]> {
    const url = `${this.baseUrl}/${orgId}/codes`;
    return this.http.get<string[]>(url);
  }

  /**
   * 驗證單一品牌權限
   * 對應後端: GET /validate?orgId=xxx&internalBrandCode=xxx
   * @param orgId 公司代碼
   * @param internalBrandCode 內部品牌代碼
   * @returns boolean (true: 通過, false: 失敗)
   */
  validate(orgId: string, internalBrandCode: string): Observable<boolean> {
    const url = `${this.baseUrl}/validate`;

    const params = new HttpParams()
      .set('orgId', orgId)
      .set('internalBrandCode', internalBrandCode);

    return this.http.get<boolean>(url, { params });
  }

  /**
   * 批次驗證品牌權限 (用於 Excel 匯入或大量檢查)
   * 對應後端: POST /validate/batch
   * @param pairs 待驗證的清單
   * @returns ValidationResult[] 驗證結果清單
   */
  batchValidate(pairs: OrgInternalBrandPair[]): Observable<ValidationResult<OrgInternalBrandPair>[]> {
    const url = `${this.baseUrl}/validate/batch`;
    return this.http.post<ValidationResult<OrgInternalBrandPair>[]>(url, pairs);
  }

  // =================================================================
  // Region: 維護與管理 (Write Operations)
  // =================================================================

  /**
   * 新增/啟用 品牌權限
   * 對應後端: POST /
   */
  addBrandMapping(pair: OrgInternalBrandPair): Observable<string> {
    return this.http.post(this.baseUrl, pair, { responseType: 'text' });
  }

  /**
   * 停用品牌權限
   * 對應後端: PUT /
   */
  deactivateBrandMapping(pair: OrgInternalBrandPair): Observable<string> {
    return this.http.put(this.baseUrl, pair, { responseType: 'text' });
  }

  /**
   * 手動刷新 Cache (Ops/Admin 用)
   * 對應後端: POST /refresh-cache
   * @returns Object 包含執行時間與狀態
   */
  refreshCache(): Observable<any> {
    const url = `${this.baseUrl}/refresh-cache`;
    // POST 請求，Body 為空物件
    return this.http.post<any>(url, {});
  }
}
